import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import { Plus, Edit, Trash2, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const KittySchemes = ({ user }) => {
  const navigate = useNavigate();
  const [schemes, setSchemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('Active');

  useEffect(() => {
    loadSchemes();
  }, [filter]);

  const loadSchemes = async () => {
    try {
      const params = filter !== 'All' ? `?status=${filter}` : '';
      const res = await axiosInstance.get(`/kitty/schemes${params}`);
      setSchemes(res.data);
    } catch (err) {
      toast.error('Error loading schemes');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (schemeId) => {
    if (!window.confirm('Deactivate this scheme? Active enrollments will not be affected.')) return;
    
    try {
      await axiosInstance.delete(`/kitty/schemes/${schemeId}`);
      toast.success('Scheme deactivated');
      loadSchemes();
    } catch (err) {
      toast.error('Error deactivating scheme');
    }
  };

  const effectiveRole = user.effective_role || user.role;
  if (effectiveRole !== 'Admin' && effectiveRole !== 'Manager') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 p-4">
        <div className="max-w-2xl mx-auto mt-20 text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h2>
          <p className="text-gray-600">Only Admin/Manager can manage schemes</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 pb-32">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white shadow-md border-b-2 border-amber-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-bold text-gray-800">Kitty Schemes</h1>
          <button
            onClick={() => navigate('/kitty/schemes/create')}
            className="px-4 py-2 bg-gradient-to-r from-amber-400 to-orange-400 text-white rounded-lg font-semibold hover:shadow-lg transition-all flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Create Scheme
          </button>
        </div>

        {/* Filters */}
        <div className="flex gap-2 mt-3 overflow-x-auto">
          {['All', 'Active', 'Inactive'].map(status => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap ${
                filter === status
                  ? 'bg-amber-500 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {status}
            </button>
          ))}
        </div>
      </div>

      {/* Schemes List */}
      <div className="p-4 space-y-3">
        {loading ? (
          <div className="text-center py-8">
            <p className="text-gray-600">Loading schemes...</p>
          </div>
        ) : schemes.length === 0 ? (
          <div className="text-center py-8 bg-white rounded-xl border-2 border-amber-200">
            <p className="text-gray-600">No schemes found</p>
          </div>
        ) : (
          schemes.map(scheme => (
            <div key={scheme.scheme_id} className="premium-card border-2 border-amber-200 p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold text-gray-800">{scheme.name}</h3>
                    {scheme.status === 'Active' ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <XCircle className="w-4 h-4 text-gray-400" />
                    )}
                  </div>
                  <p className="text-xs text-gray-600">
                    {scheme.periods_customer_pays} + {scheme.periods_bonus_company} ({scheme.frequency})
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => navigate(`/kitty/schemes/${scheme.scheme_id}/edit`)}
                    className="p-2 hover:bg-amber-100 rounded-lg"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  {scheme.status === 'Active' && (
                    <button
                      onClick={() => handleDelete(scheme.scheme_id)}
                      className="p-2 hover:bg-red-100 rounded-lg text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-gray-600">Payment Mode:</span>
                  <span className="ml-1 font-semibold">{scheme.payment_mode.replace('_', ' ')}</span>
                </div>
                <div>
                  <span className="text-gray-600">Bonus Rule:</span>
                  <span className="ml-1 font-semibold">{scheme.bonus_rule.replace('BONUS_', '').replace('_', ' ')}</span>
                </div>
                <div>
                  <span className="text-gray-600">Rate Mode:</span>
                  <span className="ml-1 font-semibold">{scheme.rate_booking_mode.replace('_', ' ')}</span>
                </div>
                <div>
                  <span className="text-gray-600">Grace Days:</span>
                  <span className="ml-1 font-semibold">{scheme.grace_days_for_due}</span>
                </div>
              </div>

              <div className="mt-2 flex flex-wrap gap-1">
                {scheme.redemption_options?.map(opt => (
                  <span key={opt} className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                    {opt.replace('REDEEM_', '')}
                  </span>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default KittySchemes;